<script setup lang="ts">
  import { defineProps } from 'vue'

  import { Media } from '@/types.ts'

  const props = defineProps({
    medias: {
      type: Array<Media>(),
      required: true,
    },
    selection: {
      type: Set<number>,
      default: () => new Set(),
    },
  })

  const emit = defineEmits(['select', 'toggle'])

  const isSelected = (media: Media) => {
    return props.selection.has(media.id)
  }

  const handleClick = (event: MouseEvent, media: Media) => {
    if (event.metaKey || event.ctrlKey) {
      emit('toggle', media.id)
    } else {
      emit('select', media.id)
    }
  }
</script>

<template>
  <div class="grid 3xl:grid-cols-6 2xl:grid-cols-5 xl:grid-cols-4 lg:grid-cols-3 sm:grid-cols-2 gap-4 overflow-y-auto p-2">
    <div v-for="(media, index) in props.medias" :key="index" class="relative group">
      <button @click.prevent="handleClick($event, media)" class="w-full">
        <div class="w-full aspect-square bg-white rounded overflow-hidden flex items-center justify-center" :class="{ 'outline outline-4 outline-info': isSelected(media) }">
          <img class="w-full h-full object-contain" loading="lazy" onerror="this.src='/images/loading_error.png'" :alt="media.data.alt" :title="media.data.title" :src="`${media.url}?fit=crop&w=300&h=300&auto=compress`">
        </div>
        <div class="text-center hyphens-auto mx-auto max-w-36 mt-2">
          <p class="text-xs">{{ media.name }}</p>
        </div>
      </button>
      <!-- Selected: filled circle with check (click = toggle) -->
      <div class="absolute top-0 right-0 m-1 cursor-pointer" v-if="isSelected(media)" @click.stop.prevent="emit('toggle', media.id)">
        <svg class="h-7 w-7 fill-info" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" /></svg>
      </div>
      <!-- Not selected: empty circle on hover (click = toggle) -->
      <div class="absolute top-0 right-0 m-1 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity" v-else @click.stop.prevent="emit('toggle', media.id)">
        <svg class="h-7 w-7 fill-none stroke-info stroke-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /></svg>
      </div>
    </div>
  </div>
</template>
